import { doc, runTransaction, serverTimestamp } from "firebase/firestore";
import { db } from "../../../firebase/config";
import * as matchRepository from "../../../repositories/matchRepository";
import * as matchAccessRepository from "../../../repositories/matchAccessRepository";
import { getCurrentAuthUser } from "../../../repositories/authRepository";
import { normRegistryEntries, normRoomMembers } from "../utils/normalize";
import { mergeRegistryEntryLists, mergeRoomMemberLists } from "../utils/registryMerge";

function canWriteRegistry(match, uid) {
  if (!match || !uid) return false;
  return match.createdBy === uid || match.deputyRefereeUid === uid;
}

/**
 * After merge, drop presence for users whose roster row was deactivated (kick/remove).
 * @param {import('../models/registryTypes').RegistryEntry[]} entries
 * @param {import('../models/registryTypes').RoomMember[]} members
 */
export function pruneMembersForInactiveEntries(entries, members) {
  const inactiveUserIds = new Set(
    (entries || [])
      .filter((e) => e && e.active === false && e.userId)
      .map((e) => e.userId)
  );
  if (!inactiveUserIds.size) return members || [];
  return (members || []).filter((m) => !inactiveUserIds.has(m.userId));
}

async function assertCanPersistRegistry(matchId, uid) {
  const match = await matchRepository.fetchMatch(matchId);
  if (!match) return { ok: false, match: null };
  if (canWriteRegistry(match, uid)) return { ok: true, match };
  const grant = await matchAccessRepository.fetchAccessGrant(matchId, uid);
  if (
    grant?.role === "spectator" ||
    grant?.role === "referee" ||
    grant?.role === "player"
  ) {
    return { ok: true, match };
  }
  return { ok: false, match };
}

/**
 * Transactional roster write — merge client patch with the latest server roster
 * so concurrent invite joins are not lost to a host auto-register race.
 *
 * Grant holders may only touch registryEntries / roomMembers / updatedAt
 * (see matchGrantHolderRosterUpdateValid). Do not write `players` here.
 *
 * @param {string} matchId
 * @param {{ registryEntries: unknown[]; roomMembers?: unknown[] }} payload
 */
export async function persistRegistryState(matchId, payload) {
  const uid = getCurrentAuthUser()?.uid;
  if (!uid || !matchId) return null;

  const allowed = await assertCanPersistRegistry(matchId, uid);
  if (!allowed.ok) return null;

  const clientEntries = normRegistryEntries(payload.registryEntries || [], matchId);
  const clientMembers = normRoomMembers(payload.roomMembers || [], matchId);
  const ref = doc(db, "matches", matchId);

  try {
    return await runTransaction(db, async (transaction) => {
      const snap = await transaction.get(ref);
      if (!snap.exists()) return null;
      const data = snap.data() || {};
      const serverEntries = normRegistryEntries(data.registryEntries || [], matchId);
      const serverMembers = normRoomMembers(data.roomMembers || [], matchId);

      let registryEntries = mergeRegistryEntryLists(serverEntries, clientEntries).map(
        ({ gender: _gender, ...entry }) => entry
      );
      let roomMembers = mergeRoomMemberLists(serverMembers, clientMembers);
      roomMembers = pruneMembersForInactiveEntries(registryEntries, roomMembers);

      // Grant-holder rules allow only these keys — never include `players`.
      transaction.update(ref, {
        registryEntries,
        roomMembers,
        updatedAt: serverTimestamp(),
      });
      return {
        registryEntries,
        roomMembers,
      };
    });
  } catch (err) {
    console.warn("[registry] transactional persist failed", err?.message || err);
    return null;
  }
}

/**
 * @param {string} matchId
 */
export async function fetchRegistryState(matchId) {
  const match = await matchRepository.fetchMatch(matchId);
  if (!match) return { registryEntries: [], roomMembers: [] };
  return {
    registryEntries: match.registryEntries || [],
    roomMembers: match.roomMembers || [],
  };
}
